'use client';

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Search } from 'lucide-react';

// The heavy dropdown panel (input, debounced fetch, result rendering) is
// code-split and only downloaded the first time the user opens search.
const SearchDropdownPanel = dynamic(
  () => import('./search-dropdown-panel').then((m) => ({ default: m.SearchDropdownPanel })),
  { ssr: false },
);

interface SearchTriggerProps {
  searchPath?: string;
  apiPath: string;
  placeholder?: string;
}

/**
 * Search entry point for the desktop navbar.
 *
 * The icon button is an ordinary client component (NOT a `dynamic(ssr:false)`
 * import), so it renders in the server HTML and hydrates in place — there is no
 * client-only flicker. Only the panel is lazy: its chunk loads on first open,
 * so tenants with search pay for the trigger up-front (tiny) and the dropdown
 * on demand.
 */
export function SearchTrigger({ searchPath = '/search', apiPath, placeholder }: SearchTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // While open, close on outside click or Escape.
  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-label="Open search"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen(true)}
        className="btn btn-ghost btn-sm"
      >
        <Search className="w-5 h-5" aria-hidden="true" />
      </button>

      {isOpen && (
        <SearchDropdownPanel
          apiPath={apiPath}
          searchPath={searchPath}
          placeholder={placeholder}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
